/**
 * CompetitorCAGRChart — Revenue CAGR comparison across the focal company
 * and its peers. Bars sorted by CAGR descending. The focal company is
 * highlighted in the selected colour.
 */
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  ReferenceLine,
  LabelList,
} from 'recharts';
import ChartCard, { FocalBadge, focalTickFactory } from './ChartCard';
import ChartTooltip from './ChartTooltip';
import { PEER_BAR_UNIFORM, BAR_MAX_SIZE, gridProps, yAxisProps, zeroLineProps, CHART_INK, CHART_TYPE } from './chartTheme';
import type { UnifiedFinancialRecord, RegionConfig } from '../../../types/financial';

interface Props {
  data: UnifiedFinancialRecord[];
  companyName: string;
  competitorData: Record<string, UnifiedFinancialRecord[]>;
  region?: RegionConfig;
  years?: number;
}

function revenueOf(rec: UnifiedFinancialRecord): number | null {
  return rec.total_revenue ?? rec.revenue_from_operations ?? null;
}

function computeRevenueCAGR(
  records: UnifiedFinancialRecord[],
  windowYears: number,
): number | null {
  const valid = records
    .map((r) => ({ year: r.year, rev: revenueOf(r) }))
    .filter((r): r is { year: number; rev: number } => r.rev != null && r.rev > 0)
    .sort((a, b) => a.year - b.year);

  if (valid.length < 2) return null;
  const tail = valid.slice(-windowYears);
  if (tail.length < 2) return null;
  const first = tail[0].rev;
  const last = tail[tail.length - 1].rev;
  const n = tail.length - 1;
  if (first <= 0 || last <= 0 || n <= 0) return null;
  return (Math.pow(last / first, 1 / n) - 1) * 100;
}

export default function CompetitorCAGRChart({
  data,
  companyName,
  competitorData,
  years = 10,
}: Props) {
  const entries = [
    {
      company: companyName,
      cagr: computeRevenueCAGR(data, years),
      isSelected: true,
    },
    ...Object.entries(competitorData).map(([name, records]) => ({
      company: name,
      cagr: computeRevenueCAGR(records, years),
      isSelected: false,
    })),
  ]
    .filter((e): e is { company: string; cagr: number; isSelected: boolean } => e.cagr != null)
    .sort((a, b) => b.cagr - a.cagr);

  // Fewer than two comparable companies is a feature-not-available case.
  if (entries.length < 2) return null;

  return (
    <ChartCard
      title="Revenue CAGR — Peer Comparison"
      subtitle={`${years}-year compound annual growth rate.`}
      action={<FocalBadge name={companyName} />}
    >
      <ResponsiveContainer width="100%" height={320}>
        <BarChart data={entries} margin={{ top: 24, right: 24, left: 8, bottom: 60 }}>
          <CartesianGrid {...gridProps} />
          <XAxis dataKey="company" interval={0} height={70} tick={focalTickFactory(companyName)} />
          <YAxis
            {...yAxisProps}
            width={56}
            tickFormatter={(v) => `${v.toFixed(0)}%`}
            label={{ value: 'Revenue CAGR (%)', angle: -90, position: 'insideLeft', style: { fontSize: CHART_TYPE.axisTick, fill: CHART_INK.muted } }}
          />
          <ReferenceLine y={0} {...zeroLineProps} />
          <Tooltip content={<ChartTooltip format={(v) => `${v.toFixed(2)}%`} />} cursor={{ fill: '#f9fafb' }} />
          {/* One uniform tone for every company; the focal is marked by its
              bold ★ axis tick, matching the EV Insights stacked charts. */}
          <Bar dataKey="cagr" name="CAGR (%)" radius={[4, 4, 0, 0]} maxBarSize={BAR_MAX_SIZE}>
            <LabelList
              dataKey="cagr"
              position="top"
              formatter={(v: number) => `${v.toFixed(1)}%`}
              style={{ fontSize: CHART_TYPE.dataLabel, fill: CHART_INK.label, fontWeight: 600 }}
            />
            {entries.map((entry) => (
              <Cell key={entry.company} fill={PEER_BAR_UNIFORM} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}
